import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ApolloProvider, useQuery } from '@apollo/client';
import { AuthProvider } from './admin/AuthContext';
import client from './apollo/client';
import MaintenancePage from './components/MaintenancePage';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import LoadingBar from './components/LoadingBar';
import { IS_MAINTENANCE_MODE, GET_SYSTEM_SETTINGS } from './apollo/queries/settingsQueries';
import HomePage from './pages/main/HomePage';
import AdminLayout from './admin/AdminLayout';
import AdminDashboardPage from './admin/AdminDashboardPage';
import CustomerAdminPage from './admin/customer/CustomerAdminPage';
import CustomerEditPage from './admin/customer/CustomerEditPage';
import CustomerQuotationPage from './admin/customer/CustomerQuotationPage';
import QuotationDetailPage from './admin/quotation/QuotationDetailPage';
import CustomerRegisterPage from './admin/customer/CustomerRegisterPage';
import BlockedCustomerPage from './admin/customer/BlockedCustomerPage';
import MonthlyStatsPage from './admin/stats/MonthlyStatsPage';
import CategoryManagementPage from './admin/category/CategoryManagementPage';
import CategoryRankingPage from './admin/category/CategoryRankingPage';
import ProductManagementPage from './admin/product/ProductManagementPage';
import ProductFormPage from './admin/product/ProductFormPage';
import QuotationManagementPage from './admin/quotation/QuotationManagementPage';
import QuotationPendingPage from './admin/quotation/QuotationPendingPage';
import QuotationResponsePage from './admin/quotation/QuotationResponsePage';
import ConnectionStatsPage from './admin/stats/ConnectionStatsPage';
import QuotationStatsPage from './admin/stats/QuotationStatsPage';
import SignupStatsPage from './admin/stats/SignupStatsPage';
import CustomerSupportPage from './admin/support/CustomerSupportPage';
import DesignManagementPage from './admin/design/DesignManagementPage';
import AddBannerPage from './admin/design/AddBannerPage';
import AdminSettingsPage from './admin/AdminSettingsPage';
import AdminProfilePage from './admin/AdminProfilePage';
import ShopPage from './pages/shop/ShopPage';
import ProductDetailPage from './pages/shop/ProductDetailPage';
import CartPage from './pages/shop/CartPage';
import SearchResultsPage from './pages/shop/SearchResultsPage';
import QuotationHistoryPage from './pages/order/QuotationHistoryPage';
import CustomOrderPage from './pages/order/CustomOrderPage';
import OrderGuidePage from './pages/order/OrderGuidePage';
import UserQuotationDetailPage from './pages/order/QuotationDetailPage';
import WithdrawalPage from './pages/user/WithdrawalPage';
import MyPage from './pages/user/MyPage';
import EmailChangePage from './pages/user/EmailChangePage';
import UserInfoEditPage from './pages/user/UserInfoEditPage';
import ChangePasswordPage from './pages/user/ChangePasswordPage';
import FindCredentialsPage from './pages/user/FindCredentialsPage';
import SignUpPage from './pages/user/SignUpPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import MainLayout from './components/MainLayout';
import './pages/css/HomePage.css';
import './admin/css/CustomerAdminPage.css';
import './admin/css/AdminDashboardPage.css';
import './admin/css/AdminLayout.css';
import './admin/css/CustomerQuotationPage.css';
import './admin/css/QuotationDetailPage.css';
import './admin/css/CustomerRegisterPage.css';
import './admin/css/BlockedCustomerPage.css';
import './admin/css/MonthlyStatsPage.css';
import './admin/css/CategoryManagementPage.css';
import './admin/css/CategoryRankingPage.css';
import './admin/css/ProductManagementPage.css';
import './admin/css/ProductFormPage.css';
import './admin/css/QuotationManagementPage.css';
import './admin/css/QuotationPendingPage.css';
import './admin/css/QuotationResponsePage.css';
import './pages/css/ShopPage.css';
import './pages/css/ProductDetailPage.css';
import './pages/css/CartPage.css';
import './pages/css/SearchResultsPage.css';
import './pages/css/QuotationHistoryPage.css';
import './pages/css/CustomOrderPage.css';
import './pages/css/WithdrawalPage.css';
import './pages/css/MyPage.css';
import './pages/css/EmailChangePage.css';
import './pages/css/UserInfoEditPage.css';
import './pages/css/ChangePasswordPage.css';
import './pages/css/OrderGuidePage.css';
import './pages/css/QuotationDetailPage.css';
import './pages/css/SignUpPage.css';
import './admin/css/CustomerSupportPage.css';
import './admin/css/DesignManagementPage.css';
import './admin/css/AddBannerPage.css';
import './admin/css/AdminSettingsPage.css';
import './admin/css/AdminProfilePage.css';
import './admin/css/AdminCommon.css';
import './components/css/MaintenancePage.css';
import './components/css/LoadingBar.css';
import './components/css/GlobalFixes.css';

const AppContent = () => {
  const location = useLocation();
  const { data: maintenanceData, loading } = useQuery(IS_MAINTENANCE_MODE);
  const { data: settingsData } = useQuery(GET_SYSTEM_SETTINGS);

  // 파비콘 동적 업데이트
  React.useEffect(() => {
    if (settingsData?.getSystemSettings?.faviconUrl) {
      const favicon = settingsData.getSystemSettings.faviconUrl;
      
      // 기존 파비콘 링크 요소들 제거
      const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
      existingFavicons.forEach(link => link.remove());
      
      // 새 파비콘 링크 요소 생성
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = favicon;
      
      // 파일 확장자에 따른 타입 설정
      if (favicon.toLowerCase().includes('.ico')) {
        link.type = 'image/x-icon';
      } else if (favicon.toLowerCase().includes('.png')) {
        link.type = 'image/png';
      } else if (favicon.toLowerCase().includes('.svg')) {
        link.type = 'image/svg+xml';
      }
      
      document.head.appendChild(link);
    }
  }, [settingsData]);

  // 관리자 페이지는 유지보수 모드에서도 접근 가능
  const isAdminPage = location.pathname.startsWith('/admin');
  const isMaintenanceMode = maintenanceData?.isMaintenanceMode;

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (isMaintenanceMode && !isAdminPage) {
    return <MaintenancePage />;
  }

  return (
    <Routes>
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/product" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/quotation-history" element={<QuotationHistoryPage />} />
              <Route path="/quotation-detail" element={<UserQuotationDetailPage />} />
              <Route path="/order-guide" element={<OrderGuidePage />} />
              <Route path="/custom-order" element={<CustomOrderPage />} />
              <Route path="/withdrawal" element={<WithdrawalPage />} />
              <Route path="/my-page" element={<MyPage />} />
              <Route path="/my-page/edit" element={<UserInfoEditPage />} />
              <Route path="/email-change" element={<EmailChangePage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/find-credentials" element={<FindCredentialsPage />} />
              <Route path="/signup" element={<SignUpPage />} />
            </Route>
            
            {/* 이용약관 및 개인정보처리방침 (별도 레이아웃 없이) */}
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="customer" element={<CustomerAdminPage />} />
              <Route path="customer/edit" element={<CustomerEditPage />} />
              <Route path="customer/quotations" element={<CustomerQuotationPage />} />
              <Route path="customer/quotation-detail" element={<QuotationDetailPage />} />
              <Route path="customer/register" element={<CustomerRegisterPage />} />
              <Route path="customer/blocked" element={<BlockedCustomerPage />} />
              <Route path="customer/stats" element={<MonthlyStatsPage />} />
              <Route path="category" element={<CategoryManagementPage />} />
              <Route path="category-ranking" element={<CategoryRankingPage />} />
              <Route path="products" element={<ProductManagementPage />} />
              <Route path="product/add" element={<ProductFormPage />} />
              <Route path="product/edit" element={<ProductFormPage />} />
              <Route path="quotes" element={<QuotationManagementPage />} />
              <Route path="quotes/pending" element={<QuotationPendingPage />} />
              <Route path="quotes/response" element={<QuotationResponsePage />} />
              <Route path="stats/connection" element={<ConnectionStatsPage />} />
              <Route path="stats/quotation" element={<QuotationStatsPage />} />
              <Route path="stats/signup" element={<SignupStatsPage />} />
              <Route path="support" element={<CustomerSupportPage />} />
              <Route path="design" element={<DesignManagementPage />} />
              <Route path="design/add" element={<AddBannerPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="profile" element={<AdminProfilePage />} />
            </Route>
          </Routes>
  );
};

function App() {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <LoadingProvider>
          <Router future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}>
            <AppContent />
            <LoadingBar />
          </Router>
        </LoadingProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;
